import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import Button from "../../../components/common/button";
import { Input } from "../../../components/ui/input";
import { db } from "../../../config/firebaseConfig";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { GripHorizontal, Trash } from "lucide-react";
import Modal from "../../../components/common/modal";
import { fetchBankLeadData } from "../../../api";
import { Indexing } from "./indexing";
import { formatDate } from "../../../lib/utils";
import { Endringsmelding } from "./endringsmelding";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Spinner } from "../../../components/Spinner";

const sectionSchema = z.object({
  startDate: z.string().min(1, "Forventet oppstart er påkrevd"),
  endDate: z.string().min(1, "Forventet utført er påkrevd"),
  pris: z.string().min(1, "Utbetaling ihht. faktureringsplan er påkrevd"),
  commenter: z.string().min(1, "Kommentar er påkrevd"),
  navn: z.string().min(1, "Navn er påkrevd"),
  status: z.string().min(1, "Status er påkrevd"),
  id: z.string(),
});

const formSchema = z.object({
  FremdriftsplanOg: z
    .array(sectionSchema)
    .min(1, "Minst ett produkt er påkrevd."),
});

export type FremdriftsplanHandle = {
  validateForm: () => Promise<boolean>;
};

export const FremdriftsplanOg: React.FC<{
  setActiveTab: any;
  getData: any;
}> = ({ setActiveTab, getData }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const form = useForm<any>({
    resolver: zodResolver(formSchema),
    defaultValues: { FremdriftsplanOg: [] },
  });
  const [loading, setLoading] = useState(false);

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "FremdriftsplanOg",
  });
  const [IndexreguleringData, setIndexreguleringData] = useState<any>(null);
  const [EndringsmeldingData, setEndringsmeldingData] = useState<any>(null);

  const removeProduct = (index: number) => {
    remove(index);
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      const docRef = doc(db, "bank_leads", String(id));

      const existingData = (await getDoc(docRef)).data()?.Fremdriftsplan || {};
      await updateDoc(docRef, {
        Fremdriftsplan: { ...existingData, ...data },
        updatedAt: formatDate(new Date()),
      });
      toast.success("Lagret", { position: "top-right" });

      setActiveTab(2);
      getData();
      setLoading(false);
    } catch (error) {
      console.error("Firestore operation failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const loadData = async () => {
      const data = await fetchBankLeadData(id);

      if (data?.Fremdriftsplan?.Indexing) {
        setIndexreguleringData(data?.Fremdriftsplan?.Indexing);
      }
      if (data?.Fremdriftsplan?.Indexing) {
        setEndringsmeldingData(data?.Fremdriftsplan?.Endringsmelding);
      }

      const filteredEntries = Object.entries(data?.Fremdriftsplan || {}).filter(
        ([key]) => key !== "Indexing" && key !== "Endringsmelding"
      );

      if (data?.Fremdriftsplan?.FremdriftsplanOg) {
        form.setValue("FremdriftsplanOg", data.Fremdriftsplan.FremdriftsplanOg);
      } else if (data?.Fremdriftsplan && filteredEntries) {
        const stepsArray: any[] = [];

        const entries = Object.entries(data?.Fremdriftsplan).filter(
          ([key]) => key !== "id"
        );

        const sorted: any = entries.sort(
          (a: any, b: any) => a[1].order - b[1].order
        );

        for (const [stepName, stepData] of sorted) {
          const startDate = stepData.date;
          const daysToAdd = Number(stepData.day) || 0;

          const endDate = (() => {
            if (!startDate || daysToAdd === 0) return startDate;
            const d = new Date(startDate);
            d.setDate(d.getDate() + daysToAdd);
            return d.toISOString().split("T")[0];
          })();

          stepsArray.push({
            startDate,
            endDate,
            pris: stepData.pris || "",
            commenter: "",
            navn: stepName,
            status: stepData.status || "Unpaid",
            id: id,
          });
        }

        form.setValue("FremdriftsplanOg", stepsArray);
      }
      setLoading(false);
    };

    loadData();
  }, [form, id]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("dragIndex", index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number
  ) => {
    e.preventDefault();
    const dragIndex = Number(e.dataTransfer.getData("dragIndex"));
    if (dragIndex === dropIndex) return;
    move(dragIndex, dropIndex);
  };

  const [addStep, setAddStep] = useState(false);
  const [stepName, setStepName] = useState("");

  const calculateTotalPrice = () => {
    const allPrices = form.watch("FremdriftsplanOg");
    if (!allPrices || allPrices.length === 0) return 0;

    return allPrices.reduce((total: number, item: any) => {
      const price = item.pris ? parseInt(item.pris.replace(/\D/g, ""), 10) : 0;
      return total + (isNaN(price) ? 0 : price);
    }, 0);
  };

  const totalPrice = calculateTotalPrice();

  const formatToNOK = (amount: number) => {
    return new Intl.NumberFormat("no-NO").format(amount) + " NOK";
  };

  const [Indexregulering, setIndexregulering] = useState(false);
  const [endringsmelding, setEndringsmelding] = useState(false);

  return (
    <>
      {loading && <Spinner />}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="rounded-lg bg-lightGreen mb-20 w-full">
            <div className="p-3 md:p-4">
              <h3 className="text-base lg:text-lg font-semibold text-black3 mb-2">
                Fremdrifts- og faktureringsplan
              </h3>
              <p className="text-sm text-gray4">
                <span className="font-bold">
                  Her legge du inn forventet tidsbruk i antall dager
                </span>{" "}
                (medregnet helger og helligdager)
              </p>
            </div>
            <div className="p-1 pt-0">
              <div className="bg-white rounded-lg border border-gray3 shadow-shadow4">
                <div>
                  {fields.map((item: any, index) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-3 md:gap-4 p-3 md:p-4 border-b border-gray3"
                    >
                      <div className="flex items-center gap-3 md:gap-4 justify-between">
                        <div className="flex items-center gap-3 md:gap-4">
                          <div
                            className="cursor-grab active:cursor-grabbing"
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                          >
                            <GripHorizontal className="text-primary" />
                          </div>
                          <h4 className="font-medium text-sm md:text-base text-black3">
                            Step {index + 1}:{" "}
                            <span className="font-semibold">{item?.navn}</span>
                          </h4>
                        </div>
                        <div>
                          <Trash
                            className="text-red cursor-pointer hover:opacity-70"
                            onClick={() => {
                              removeProduct(index);
                            }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 desktop:grid-cols-4 gap-3 md:gap-4 lg:gap-5 flex-1">
                        <FormField
                          control={form.control}
                          name={`FremdriftsplanOg.${index}.startDate`}
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <p
                                className={`${
                                  fieldState.error ? "text-red" : ""
                                } mb-[6px] text-sm`}
                              >
                                Forventet oppstart
                              </p>
                              <FormControl>
                                <Input
                                  placeholder="Skriv inn Forventet oppstart"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="date"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`FremdriftsplanOg.${index}.endDate`}
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <p
                                className={`${
                                  fieldState.error ? "text-red" : ""
                                } mb-[6px] text-sm`}
                              >
                                Forventet utført
                              </p>
                              <FormControl>
                                <Input
                                  placeholder="Skriv inn Forventet utført"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="date"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`FremdriftsplanOg.${index}.pris`}
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <p
                                className={`${
                                  fieldState.error ? "text-red" : ""
                                } mb-[6px] text-sm`}
                              >
                                Utbetaling ihht. faktureringsplan
                              </p>
                              <FormControl>
                                <Input
                                  placeholder="Skriv inn Utbetaling ihht. faktureringsplan"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                                  inputMode="numeric"
                                  type="text"
                                  onChange={({ target: { value } }: any) =>
                                    field.onChange({
                                      target: {
                                        name: `pris`,
                                        value: value.replace(/\D/g, "")
                                          ? new Intl.NumberFormat(
                                              "no-NO"
                                            ).format(
                                              Number(value.replace(/\D/g, ""))
                                            )
                                          : "",
                                      },
                                    })
                                  }
                                  value={
                                    field.value === null ? "-" : field.value
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`FremdriftsplanOg.${index}.commenter`}
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <p
                                className={`${
                                  fieldState.error ? "text-red" : ""
                                } mb-[6px] text-sm`}
                              >
                                Kommentar
                              </p>
                              <FormControl>
                                <Input
                                  placeholder="Skriv inn Kommentar"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="p-3 md:p-4">
                    <div
                      className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2 hover:opacity-90 w-full sm:w-max cursor-pointer justify-center"
                      onClick={() => setAddStep(true)}
                    >
                      + Legg til steg
                    </div>
                  </div>
                  <div className="border-t border-gray3 w-full"></div>
                  <div className="flex flex-col gap-3 md:gap-4 p-3 md:p-4 bg-skyBlue">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1">
                      <p className="text-gray4 text-xs md:text-sm">
                        Kontraktsum
                      </p>
                      <p className="text-black3 text-sm md:text-base">
                        {formatToNOK(totalPrice)}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1">
                      <p className="text-gray4 text-xs md:text-sm">
                        Indeksregulering{" "}
                        {IndexreguleringData?.date
                          ? `(${formatDate(IndexreguleringData?.date)})`
                          : ""}
                      </p>
                      <p className="text-black3 text-sm md:text-base">
                        {IndexreguleringData?.pris ?? 0} NOK
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1 mt-1">
                      <p className="text-black3 text-sm md:text-base font-medium">
                        Endringsmelding{" "}
                        {EndringsmeldingData?.date
                          ? `(${formatDate(EndringsmeldingData?.date)})`
                          : ""}
                      </p>
                      <p className="text-black3 text-base md:text-lg lg:text-xl font-semibold">
                        {EndringsmeldingData?.pris ?? 0} NOK
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end sm:items-center gap-3 md:gap-4 p-4 md:p-5">
                  <div
                    className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2 justify-center"
                    onClick={() => setIndexregulering(true)}
                  >
                    Indexregulering
                  </div>
                  <div
                    className="border border-primary bg-primary text-white text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2 justify-center"
                    onClick={() => setEndringsmelding(true)}
                  >
                    + Lag endringsmelding
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white pb-2 bottom-0 fixed w-[calc(100%-16px)] left-2">
            <div className="flex justify-end w-full gap-4 items-center  bg-white z-50 border border-gray3 p-3 md:p-4">
              <Button
                text="Avbryt"
                className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                onClick={() => setActiveTab(0)}
              />
              <div id="submit">
                <Button
                  text="Neste"
                  className="border border-primary bg-primary text-white text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                  type="submit"
                />
              </div>
            </div>
          </div>
        </form>
      </Form>

      {addStep && (
        <Modal
          isOpen={true}
          onClose={() => setAddStep(false)}
          outSideClick={true}
        >
          <div className="bg-white rounded-lg shadow-lg w-[94vw] sm:w-[570px]">
            <h3 className="text-darkBlack font-semibold text-base md:text-lg lg:text-xl p-5 border-b border-gray4">
              Legg til steg
            </h3>

            <div className="p-5 flex flex-col gap-1">
              <label className="text-sm text-darkBlack">
                Navn på nytt steg
              </label>
              <Input
                placeholder="Skriv inn navn på steg"
                value={stepName}
                onChange={(e: any) => setStepName(e.target.value)}
                className="border border-gray1 rounded-md px-3 py-2 text-sm text-black"
              />
            </div>

            <div className="flex justify-end gap-3 px-5 pb-5">
              <Button
                text="Avbryt"
                className="border border-gray2 text-black text-sm rounded-[8px] h-[36px] px-4"
                onClick={() => setAddStep(false)}
              />
              <Button
                text="Lagre"
                className="border border-primary bg-primary text-white text-sm rounded-[8px] h-[36px] px-4"
                onClick={() => {
                  if (stepName.trim() !== "") {
                    append({
                      startDate: "",
                      endDate: "",
                      pris: "",
                      commenter: "",
                      navn: stepName,
                      status: "Unpaid",
                      id: `${stepName}-${Date.now()}-${Math.random()
                        .toString(36)
                        .substr(2, 9)}`,
                    });
                    setStepName("");
                    setAddStep(false);
                  } else {
                    toast.error("Skriv inn navn på steg", {
                      position: "top-right",
                    });
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
      {Indexregulering && (
        <Modal
          isOpen={true}
          onClose={() => setIndexregulering(false)}
          outSideClick={true}
        >
          <div className="bg-white rounded-lg shadow-lg w-[94vw] sm:w-[570px]">
            <h3 className="text-darkBlack font-semibold text-base md:text-lg lg:text-xl p-5 border-b border-gray4">
              Legg til indexing
            </h3>

            <Indexing
              Kontraktsum={totalPrice}
              setIndexregulering={setIndexregulering}
              setIndexreguleringData={setIndexreguleringData}
              IndexreguleringData={IndexreguleringData}
            />
          </div>
        </Modal>
      )}
      {endringsmelding && (
        <Modal
          isOpen={true}
          onClose={() => setEndringsmelding(false)}
          outSideClick={true}
        >
          <div className="bg-white rounded-lg shadow-lg w-[94vw] sm:w-[570px] h-full max-h-[95vh] overflow-y-auto">
            <h3 className="text-darkBlack font-semibold text-base md:text-lg lg:text-xl p-5 border-b border-gray4">
              Lag endringsmelding
            </h3>

            <Endringsmelding
              setEndringsmelding={setEndringsmelding}
              setEndringsmeldingData={setEndringsmeldingData}
              EndringsmeldingData={EndringsmeldingData}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
